import React, { useCallback, useRef } from "react";
import { Task } from "@common/types/task";
import { FinancialDocument } from "@common/types/financialDocument";
import { resolveMigrationUploadSource } from "@common/helpers/migrationUploadTask";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import styles from "./FileMigrationUploadTaskBody.module.scss";
import { MigrationFileUploadTaskBody } from "./MigrationFileUploadTaskBody";
import { QbdJsonMigrationUploadTaskBody } from "./QbdJsonMigrationUploadTaskBody";

const MIGRATION_ACCEPT_TYPES = [
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

interface FileMigrationUploadTaskBodyProps {
    selectedTask: Task;
}

const MISSING_SOURCE_BODY =
    "This migration upload task is missing its source. Please contact support.";

const EmptyPanelState: React.FC<{ header: string; body: string }> = ({
    header,
    body,
}) => (
    <div className={styles.container}>
        <EmptyState header={header} body={body} />
    </div>
);

export const FileMigrationUploadTaskBody: React.FC<
    FileMigrationUploadTaskBodyProps
> = ({ selectedTask }) => {
    const onUploadRef = useRef<
        ((docs: FinancialDocument[]) => void) | undefined
    >(undefined);
    const source = resolveMigrationUploadSource(selectedTask);

    const handleUploadCompleted = useCallback(
        async (uploadedDocuments: FinancialDocument[]) => {
            onUploadRef.current?.(uploadedDocuments);
        },
        [],
    );

    if (!source) {
        return (
            <EmptyPanelState
                header="Unable to load migration upload"
                body={MISSING_SOURCE_BODY}
            />
        );
    }

    if (source === "qbdt_json") {
        return <QbdJsonMigrationUploadTaskBody selectedTask={selectedTask} />;
    }

    return (
        <FinancialDocumentUploadProvider
            accept={MIGRATION_ACCEPT_TYPES}
            onCompleted={handleUploadCompleted}
        >
            <MigrationFileUploadTaskBody
                selectedTask={selectedTask}
                source={source}
                onUploadRef={onUploadRef}
            />
        </FinancialDocumentUploadProvider>
    );
};
